// How one ecosystem orders its versions (#7789). THE RULE: the watch says `not_vulnerable` only when
// it fully understood both sides under that ecosystem's OWN scheme; everything else is `null` here,
// `unknown` in `evaluateVersion`, and `unknown` is watched. The failure it ends: a shared natural sort
// read `1.2.0.post1` as a pre-release below `1.2.0`, and `1.0.0-rc10` as past a fix at `1.0.0-rc2`,
// and each cleared a vulnerable pin.
//
// So a scheme parses only what it implements, and a suffix it does not implement is `null`, never a
// best guess. Numeric-only for an ecosystem means its qualifiers are not implemented, not that they
// are safe. Hand-kept, not `Bun.semver`: this package ships in the dashboard's bundle, where there is
// no Bun, and no semver library is in the repo. `security-version.test.ts`'s table is the pin.

type Order = -1 | 0 | 1;

const sign = (n: number): Order => (n < 0 ? -1 : n > 0 ? 1 : 0);

function compareRelease(a: readonly number[], b: readonly number[]): Order {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const delta = sign((a[i] ?? 0) - (b[i] ?? 0));
    if (delta !== 0) return delta;
  }
  return 0;
}

const numbers = (dotted: string): number[] => dotted.split('.').map(Number);

// ─── SemVer 2.0 ──────────────────────────────────────────────────────────────

type SemVer = { readonly release: readonly number[]; readonly pre: readonly string[] | null };

/**
 * One to three numbers (a feed's `< 5.0` is unambiguous), then a pre-release only on a full three.
 * `1.2.3.4`, `1.2.3rc1` and `1.2.0.post1` are not SemVer, so they are not read.
 */
function parseSemver(raw: string): SemVer | null {
  const full =
    /^v?(\d+\.\d+\.\d+)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z.-]+)?$/.exec(raw);
  if (full?.[1] !== undefined) {
    return { release: numbers(full[1]), pre: full[2]?.split('.') ?? null };
  }
  const short = /^v?(\d+(?:\.\d+)?)$/.exec(raw);
  return short?.[1] === undefined ? null : { release: numbers(short[1]), pre: null };
}

/** SemVer 11.4: numbers numerically, a number below a word, words in ASCII order: `rc10` < `rc2`. */
function compareIdentifier(a: string, b: string): Order {
  const numeric = /^\d+$/;
  if (numeric.test(a) && numeric.test(b)) return sign(Number(a) - Number(b));
  if (numeric.test(a)) return -1;
  if (numeric.test(b)) return 1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function compareSemverParsed(a: SemVer, b: SemVer): Order {
  const release = compareRelease(a.release, b.release);
  if (release !== 0) return release;
  if (a.pre === null || b.pre === null) {
    return a.pre === b.pre ? 0 : a.pre === null ? 1 : -1;
  }
  for (let i = 0; i < Math.min(a.pre.length, b.pre.length); i++) {
    const delta = compareIdentifier(a.pre[i] as string, b.pre[i] as string);
    if (delta !== 0) return delta;
  }
  return sign(a.pre.length - b.pre.length);
}

function compareSemver(a: string, b: string): Order | null {
  const left = parseSemver(a);
  const right = parseSemver(b);
  return left === null || right === null ? null : compareSemverParsed(left, right);
}

/** Pub ranks a build number, which SemVer ignores. That order is not implemented, so it is unread. */
function comparePub(a: string, b: string): Order | null {
  return a.includes('+') || b.includes('+') ? null : compareSemver(a, b);
}

// ─── PEP 440 ─────────────────────────────────────────────────────────────────

const PEP440_PRE_RANK: Readonly<Record<string, number>> = {
  a: 0,
  alpha: 0,
  b: 1,
  beta: 1,
  c: 2,
  rc: 2,
  pre: 2,
  preview: 2,
};

type Pep440 = {
  readonly release: readonly number[];
  readonly pre: readonly [number, number] | null;
};

/**
 * A release and PEP 440's pre-release spellings. NOT implemented, so not read: `.post`, `.dev`, an
 * epoch and a local `+label`. Each orders against a release in a way this does not compute.
 */
function parsePep440(raw: string): Pep440 | null {
  const match = /^v?(\d+(?:\.\d+)*)(?:[-_.]?(a|alpha|b|beta|c|rc|pre|preview)[-_.]?(\d*))?$/i.exec(
    raw,
  );
  if (match?.[1] === undefined) return null;
  const rank = match[2] === undefined ? undefined : PEP440_PRE_RANK[match[2].toLowerCase()];
  return {
    release: numbers(match[1]),
    pre: rank === undefined ? null : [rank, Number(match[3] || 0)],
  };
}

function comparePep440(a: string, b: string): Order | null {
  const left = parsePep440(a);
  const right = parsePep440(b);
  if (left === null || right === null) return null;
  const release = compareRelease(left.release, right.release);
  if (release !== 0) return release;
  if (left.pre === null || right.pre === null) {
    return left.pre === right.pre ? 0 : left.pre === null ? 1 : -1;
  }
  return compareRelease(left.pre, right.pre);
}

// ─── numeric only ────────────────────────────────────────────────────────────

/**
 * Dotted numbers, padded with zeros, and nothing else: the schemes whose qualifiers order per
 * ecosystem (Maven's `.Final`, RubyGems' `.pre`, NuGet's `-beta`, Composer's `dev-*`) and that are
 * not implemented. A pin carrying one is unread, not cleared.
 */
function compareNumeric(a: string, b: string): Order | null {
  const shape = /^v?(\d+(?:\.\d+)*)$/;
  const left = shape.exec(a)?.[1];
  const right = shape.exec(b)?.[1];
  if (left === undefined || right === undefined) return null;
  return compareRelease(numbers(left), numbers(right));
}

// ─── the dispatch ────────────────────────────────────────────────────────────

export type VersionScheme = 'semver' | 'pub' | 'pep440' | 'numeric';

const COMPARE: Readonly<Record<VersionScheme, (a: string, b: string) => Order | null>> = {
  semver: compareSemver,
  pub: comparePub,
  pep440: comparePep440,
  numeric: compareNumeric,
};

/**
 * Every ecosystem the watch can compare, closed. `image` and `helm` are absent on purpose: a tag and
 * a chart version belong to no advisory's scheme, so they read `unknown`.
 */
const ECOSYSTEM_SCHEMES: ReadonlyMap<string, VersionScheme> = new Map<string, VersionScheme>([
  ['npm', 'semver'],
  ['crates', 'semver'],
  ['go', 'semver'],
  ['hex', 'semver'],
  ['pub', 'pub'],
  ['pypi', 'pep440'],
  ['rubygems', 'numeric'],
  ['maven', 'numeric'],
  ['composer', 'numeric'],
  ['nuget', 'numeric'],
  ['cpe', 'numeric'],
]);

export const VERSION_ECOSYSTEMS: readonly string[] = [...ECOSYSTEM_SCHEMES.keys()];

/** The scheme an ecosystem's versions follow, or `null`: nothing of it can be compared. */
export function versionSchemeOf(ecosystem: string): VersionScheme | null {
  return ECOSYSTEM_SCHEMES.get(ecosystem.toLowerCase()) ?? null;
}

/** -1, 0 or 1 under that ecosystem's scheme, or `null`: not fully read, and never a guess. */
export function compareVersions(ecosystem: string, a: string, b: string): Order | null {
  const scheme = versionSchemeOf(ecosystem);
  return scheme === null ? null : COMPARE[scheme](a.trim(), b.trim());
}
